import React, { useState } from "react"
import {
  Badge,
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap"
import { useTranslation } from "react-i18next"

const statusColor = status => {
  switch (status) {
    case "approved":
      return "success"
    case "rejected":
      return "danger"
    case "pending":
      return "warning"
    default:
      return "secondary"
  }
}

const translateDocStatus = (status, t) => {
  if (!status) return t("Pending")
  const key = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
  if (key === "Verified") return t("Verified")
  return t(key, { defaultValue: status })
}

const DocumentVerificationModal = ({
  isOpen,
  toggle,
  document,
  onApprove,
  onReject,
}) => {
  const { t } = useTranslation()
  const [rejectReason, setRejectReason] = useState("")
  const [showRejectInput, setShowRejectInput] = useState(false)

  const handleApprove = () => {
    setShowRejectInput(false)
    setRejectReason("")
    onApprove(document)
  }

  const handleRejectSubmit = () => {
    onReject(document, rejectReason)
    setRejectReason("")
    setShowRejectInput(false)
  }

  const handleClose = () => {
    setRejectReason("")
    setShowRejectInput(false)
    toggle()
  }

  if (!document) return null

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="md" centered>
      <ModalHeader toggle={handleClose}>{t("Verify Document")}</ModalHeader>
      <ModalBody>
        <div className="mb-3 d-flex justify-content-between align-items-center">
          <div>
            <p className="mb-1">
              <strong>{t("Type")}:</strong>{" "}
              {t(document.type || "Document", { defaultValue: document.type || t("Document") })}
            </p>
            <p className="mb-1">
              <strong>{t("Status")}:</strong>{" "}
              <Badge color={statusColor(document.status?.toLowerCase())}>
                {translateDocStatus(document.status, t)}
              </Badge>
            </p>
            <p className="mb-0">
              <strong>{t("Uploaded")}:</strong>{" "}
              {document.uploadedAt
                ? new Date(document.uploadedAt).toLocaleDateString()
                : t("N/A")}
            </p>
          </div>
        </div>

        <div
          className="border rounded d-flex align-items-center justify-content-center bg-light mb-3"
          style={{ height: "200px" }}
        >
          {document.url ? (
            <img
              src={document.url}
              alt={document.type}
              style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
            />
          ) : (
            <div className="text-center text-muted">
              <i className="bx bx-file font-size-48 d-block mb-2"></i>
              <span>{t("No preview available")}</span>
            </div>
          )}
        </div>

        {showRejectInput && (
          <FormGroup>
            <Label>{t("Rejection Reason")}</Label>
            <Input
              type="textarea"
              rows={3}
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder={t("Enter reason for rejection...")}
            />
          </FormGroup>
        )}
      </ModalBody>
      <ModalFooter>
        {!showRejectInput ? (
          <>
            <Button color="success" onClick={handleApprove}>
              <i className="bx bx-check me-1"></i>{t("Approve")}
            </Button>
            <Button color="danger" outline onClick={() => setShowRejectInput(true)}>
              <i className="bx bx-x me-1"></i>{t("Reject")}
            </Button>
          </>
        ) : (
          <>
            <Button
              color="danger"
              onClick={handleRejectSubmit}
              disabled={!rejectReason.trim()}
            >
              {t("Confirm Rejection")}
            </Button>
            <Button color="light" onClick={() => setShowRejectInput(false)}>
              {t("Back")}
            </Button>
          </>
        )}
        <Button color="secondary" outline onClick={handleClose}>
          {t("Close")}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default DocumentVerificationModal
